"use client";

import { useScroll, useTransform, useSpring } from "motion/react";
import * as m from "motion/react-m";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Briefcase, GraduationCap } from "lucide-react";
import { MagicCard } from "#/components/magicui/magic-card";
import { Card, CardContent } from "#/components/ui/card";

interface TimelineEntry {
  title: string;
  content: React.ReactNode;
  icon?: "briefcase" | "education";
}

export const Timeline = ({ data }: { data: TimelineEntry[] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 30%", "end 50%"],
  });

  const heightTransform = useSpring(useTransform(scrollYProgress, [0, 1], [0, height]), {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });
  const opacityTransform = useSpring(useTransform(scrollYProgress, [0, 0.1], [0, 1]), {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <div className="w-full bg-white dark:bg-neutral-950 font-sans" ref={containerRef}>
      <div ref={ref} className="relative max-w-7xl mx-auto pb-20">
        {data.map((item, index) => (
          <m.div
            key={index}
            className="flex justify-start pt-10 md:pt-20 md:gap-10"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <div
              className="sticky flex flex-col md:flex-row z-30 items-center
            top-40 self-start max-w-[260px] md:w-full"
            >
              <m.div
                className="h-10 absolute left-3 md:left-3 w-10 rounded-full
                bg-white dark:bg-black flex items-center justify-center"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.3,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                <div
                  className="h-8 w-8 rounded-full bg-neutral-100
                dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 flex items-center justify-center"
                >
                  {item.icon === "briefcase" ? (
                    <Briefcase className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
                  ) : (
                    <GraduationCap className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
                  )}
                </div>
              </m.div>
              <h3
                className="hidden md:block text-sm md:pl-20 md:text-base font-medium
              text-neutral-500 dark:text-neutral-500"
              >
                {item.title}
              </h3>
            </div>

            <div className="relative w-full pl-20 md:pl-0">
              <h3 className="md:hidden block text-sm mb-4 text-left font-medium text-neutral-500 dark:text-neutral-500">
                {item.title}
              </h3>
              <m.div
                initial={{ x: 20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <Card className="p-0 shadow-none border-none">
                  <MagicCard gradientColor={"#00d49220"} className="h-full">
                    <CardContent className="p-4 md:p-6">{item.content}</CardContent>
                  </MagicCard>
                </Card>
              </m.div>
            </div>
          </m.div>
        ))}
        <div
          style={{
            height: height + "px",
          }}
          className="absolute md:left-8 left-8 top-0 overflow-hidden w-[2px]
           bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))]
            from-transparent from-[0%] via-neutral-200 dark:via-neutral-700 to-transparent to-[99%]
            [mask-image:linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)] "
        >
          <m.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px]
            bg-gradient-to-t from-emerald-500 via-teal-500 to-transparent from-[0%] via-[10%] rounded-full"
          />
        </div>
      </div>
    </div>
  );
};
